import { useCallback, useRef } from 'react';
import type { AgentRequestDto } from '@tiendanube/common';
import type { UseAgentChatQueryResult } from './types';
import { useLazyAgentChatQuery } from '../../aiApi';

/**
 * Handles and manages Agent Chat requests.
 * Includes abort functionality to cancel ongoing requests using RTK Query's built-in mechanism.
 */
function useAgentChatQuery(): UseAgentChatQueryResult {
  const [chatQuery, { data, isFetching: isLoading, error }] =
    useLazyAgentChatQuery();
  const abortRef = useRef<(() => void) | null>(null);

  const sendMessage = useCallback(
    async (options: AgentRequestDto) => {
      const promise = chatQuery(options);
      abortRef.current = promise.abort;

      try {
        await promise;
      } finally {
        abortRef.current = null;
      }
    },
    [chatQuery],
  );

  const abortRequest = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }
  }, []);

  return {
    sendMessage,
    data,
    isLoading,
    error,
    abortRequest,
  };
}

export default useAgentChatQuery;
